import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type {
  BoardViewResponse,
  Goal,
  GoalActivity,
  SessionTicketMapping,
  SessionWorkItem,
  Team,
  WorkOrdering,
} from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';

export function useTeams() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<Team[], Error>({
    queryKey: ['work', 'teams'],
    queryFn: () => getSDK().workManagement.listTeams(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useBoardView(teamId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<BoardViewResponse, Error>({
    queryKey: ['work', 'board', teamId],
    queryFn: () => getSDK().workManagement.getBoardView(teamId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && teamId),
  });
}

export function useTicketForSession(sessionId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<SessionTicketMapping | null, Error>({
    queryKey: ['work', 'ticket-by-session', sessionId],
    queryFn: () => getSDK().workManagement.getTicketBySession(sessionId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && sessionId),
  });
}

export function useSessionWorkItems(sessionIds: string[]) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const ids = useMemo(() => [...sessionIds].sort(), [sessionIds]);

  return useQuery<SessionWorkItem[], Error>({
    queryKey: ['work', 'work-items-by-sessions', ids.join(',')],
    queryFn: () => getSDK().workManagement.getSessionWorkItems(ids),
    enabled: Boolean(isAuthenticated && sdkConfigured && ids.length > 0),
  });
}

export function useGoals(teamId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<Goal[], Error>({
    queryKey: ['work', 'goals', teamId],
    queryFn: () => getSDK().workManagement.listGoals({ team_id: teamId! }),
    enabled: Boolean(isAuthenticated && sdkConfigured && teamId),
  });
}

export function useGoalActivity(goalId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<GoalActivity, Error>({
    queryKey: ['work', 'goal-activity', goalId],
    queryFn: () => getSDK().workManagement.getGoalActivity(goalId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && goalId),
  });
}

export function useWorkOrderings(goalId: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<WorkOrdering[], Error>({
    queryKey: ['work', 'work-orderings', goalId],
    queryFn: () => getSDK().workManagement.listWorkOrderings(goalId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && goalId),
  });
}

/**
 * The silent work-state feed: every ticket lane change emits under this origin
 * so a project run stepping tickets raises no toast per transition. Suppressed
 * by `notificationToastDispatcher.tsx`; emitted by
 * `go/internal/work_management/workflows.go`. Board freshness is the `/work`
 * catalog holder, not this origin.
 */
export const WORK_STATE_ORIGIN = 'WorkManagementState';
